import { UserLatestAssessmentResponse } from '@api/assessmentQuestionnaireApiHandler';
import { UserAction, useUserActionsHooks } from '@api/userActionsApiHandler';
import SpeakUpChatIcon from '@assets/images/earlyWarning/chat-icon.svg';
import FireFlameIcon from '@assets/images/icons/fire-flame-icon.svg';
import ModalContent from '@components/ModalContent';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { MotionImage, MotionText } from '@shared/atoms';
import { CreateSpeakUp, SystemButton } from '@shared/molecules';
import { CreateAction } from '@shared/organisms/Plans/CreateAction/CreateAction';
import React, { useState } from 'react';
import Modal from 'react-modal';
import { useHistory } from 'react-router';
import styles from './index.module.scss';

interface JourneySectionProps {
  engagementPoints: number;
  hoursCompleted: number;
  actionsCompleted: number;
  currentTier: string;
  nextAction: UserAction | null;
  currentStreak: number;
  refetchActions: () => void;
  nextAssessment: UserLatestAssessmentResponse | null;
}

export const JourneySection: React.FC<JourneySectionProps> = ({
  engagementPoints,
  hoursCompleted,
  actionsCompleted,
  currentTier,
  nextAction,
  currentStreak,
  refetchActions,
  nextAssessment,
}) => {
  const history = useHistory();
  const [speakUpOpen, setSpeakUpOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState<boolean>(false);
  const { isMobile } = useWindowDimensions();
  const updateUserActionMutation = useUserActionsHooks.useUpdateUserAction();

  const handleNextActionClick = async () => {
    if (!nextAction) {
      setActionModalOpen(true);

      return;
    }

    const { id, startedAt, url, actionType } = nextAction;

    if (startedAt === null && actionType !== 'USER_ACTION') {
      await updateUserActionMutation.mutateAsync({
        actionId: id,
        payload: {
          completed: null,
          dueDate: null,
          started: true,
        },
      });
    }

    history.push(url || '/improve-plans');
  };

  const handleEngagementClick = () => {
    if (nextAssessment) {
      history.push(
        `/engagement?nextAssessmentId=${nextAssessment?.id}&nextAssessment=${nextAssessment?.name}`,
      );
    } else {
      history.push('/engagement');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, callback: () => void) => {
    if (event.key === KeyConfigurations.ENTER || event.key === KeyConfigurations.SPACE) {
      event.preventDefault();
      callback();
    }
  };

  return (
    <section className={styles.journeySection} aria-labelledby="journey-heading">
      <div className={styles.journeyHeader}>
        <div>
          <MotionText
            id="journey-heading"
            className={styles.sectionTitle}
            as="h3"
            text="Your journey"
          />
        </div>

        <div className={styles.speakButton}>
          <SystemButton
            className={styles.speakUpBtn}
            variant="outlinedPrimarySkyBlue"
            size="medium"
            onClick={() => setSpeakUpOpen(true)}
            aria-label="Open speak up support chat"
          >
            <MotionImage
              src={SpeakUpChatIcon}
              alt=""
              aria-hidden="true"
              className={styles.speakUpIcon}
            />{' '}
            &nbsp; Speak Up
          </SystemButton>
        </div>
      </div>

      <div>
        <MotionText
          className={styles.journeySubtitle}
          as="text"
          text="Every step matters, no matter how small"
        />
      </div>

      <div className={styles.journeyCards} role="group" aria-labelledby="journey-cards-heading">
        <div
          className={styles.journeyCard}
          role="button"
          tabIndex={0}
          aria-labelledby="engagement-title"
          aria-describedby="engagement-description"
          onClick={handleEngagementClick}
          onKeyDown={(e) => handleKeyDown(e, handleEngagementClick)}
        >
          <MotionText id="engagement-title" as="h4" text="Engagement" />

          <div className={styles.streakCount} aria-label={`Current streak: ${currentStreak} days`}>
            <MotionImage
              src={FireFlameIcon}
              alt=""
              aria-hidden="true"
              className={styles.streakIcon}
            />
            <span aria-hidden="true">{currentStreak} days</span>
          </div>
          <div className={styles.journeyCircleEngagement}>
            <span
              className={styles.journeyValue}
              aria-label={`${engagementPoints || 0} engagement points`}
            >
              {engagementPoints || 0}
            </span>
          </div>
          <MotionText className={styles.journeyMetric} as="text" text="wellbeing points" />
          <div
            className={styles.journeyStatus}
            aria-label={`Current tier: ${currentTier || 'No tier'}`}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 32 42"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.36 23.38L16 19.86L20.62 23.38L18.86 17.68L23.5 14H17.82L16 8.38L14.18 14H8.5L13.12 17.68L11.36 23.38ZM32 16C32 7.16 24.84 0 16 0C7.16 0 0 7.16 0 16C0 20.06 1.52 23.74 4 26.56V42L16 38L28 42V26.56C30.48 23.74 32 20.06 32 16ZM16 4C22.62 4 28 9.38 28 16C28 22.62 22.62 28 16 28C9.38 28 4 22.62 4 16C4 9.38 9.38 4 16 4ZM16 34L8 36.04V29.84C10.36 31.2 13.08 32 16 32C18.92 32 21.64 31.2 24 29.84V36.04L16 34Z"
                fill="#505050"
              />
            </svg>
            <MotionText className={styles.tierName} as="text" text={currentTier || 'No tier'} />
          </div>

          <MotionText className={styles.viewProgress} as="div" text="View Progress" />
        </div>

        <div
          className={styles.journeyCard}
          aria-label="Learning hours"
          onClick={() => history.push('/posts')}
        >
          <MotionText id="learning-title" as="h4" text="Learning" />
          <div className={styles.journeyCircle}>
            <span
              className={styles.journeyValue}
              aria-label={`${hoursCompleted} learnings completed`}
            >
              {hoursCompleted}
            </span>
          </div>

          <MotionText className={styles.journeyMetric} as="text" text="learnings completed" />

          <MotionText className={styles.viewProgress} as="div" text="View Progress" />
        </div>

        <div
          className={styles.journeyCard}
          aria-label="Actions completed"
          onClick={() => history.push('/improve-plans')}
        >
          <MotionText id="actions-title" as="h4" text="Actions" />

          <div className={styles.journeyCircle}>
            <span
              className={styles.journeyValue}
              aria-label={`${actionsCompleted} actions completed`}
            >
              {actionsCompleted}
            </span>
          </div>

          <MotionText className={styles.journeyMetric} as="text" text="actions completed" />

          <MotionText className={styles.viewProgress} as="div" text="View Progress" />
        </div>

        <div
          className={`${styles.journeyCard} ${styles.nextStepCard}`}
          aria-labelledby="next-step-heading"
          onClick={handleNextActionClick}
        >
          <MotionText id="next-step-heading" as="h4" text="Suggested Next Action" />
          {nextAction && nextAction !== undefined ? (
            <>
              <p className={styles.nextStepName}>{nextAction?.title || '-'}</p>
              {/* <p className={styles.nextStepPoints}>+50 points</p> */}
              {nextAction?.actionButtonTitle ? (
                <SystemButton
                  className={styles.getStartedButton}
                  aria-label={`Get started with ${nextAction?.title || 'next action'}`}
                  size="small"
                  onClick={handleNextActionClick}
                >
                  {nextAction?.actionButtonTitle}
                </SystemButton>
              ) : (
                <div>&nbsp;</div>
              )}
            </>
          ) : (
            <>
              <p className={styles.nextStepName}>No active action found.</p>
              <SystemButton
                className={styles.getStartedCreateActionButton}
                aria-label="Get started with career assessment"
                size="small"
                onClick={() => setActionModalOpen(true)}
              >
                Create Your Action
              </SystemButton>
            </>
          )}
        </div>
      </div>

      {speakUpOpen && (
        <CreateSpeakUp
          createSpeakUpOpen={speakUpOpen}
          handleSpeakUpModalOpen={setSpeakUpOpen}
          assessmentId={null}
          questionnaireId={null}
        />
      )}

      <Modal
        isOpen={actionModalOpen}
        onRequestClose={() => setActionModalOpen(false)}
        contentLabel="Improve Action Modal"
        ariaHideApp={false}
        style={{
          content: {
            width: isMobile ? '90%' : undefined,
          },
        }}
        aria={{
          labelledby: 'action-modal-title',
          describedby: 'action-modal-description',
        }}
      >
        <ModalContent
          onClose={() => setActionModalOpen(false)}
          hasBackground
          title="Create your action"
        >
          <CreateAction
            closeModal={() => setActionModalOpen(false)}
            improvePlan="Action name"
            id={0}
            handleRefetch={refetchActions}
          />
        </ModalContent>
      </Modal>
    </section>
  );
};
